import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '../classes/api-response';
import { environment } from '../../environments/environment';
import { map } from 'rxjs/operators';
import { HttpBackend, HttpClient, HttpContext } from '@angular/common/http';
import { Contract } from "../model/contract";
import { ContractEvent } from "../model/contract-event";
import { UtilsService } from "./utils.service";
import { Constants } from "../utils/constants";
import { AuthService } from "./auth.service";
import { CreateExecutionDTO } from '../model/dto/executions/create-execution-dto';


@Injectable({
  providedIn: 'root'
})
export class ContractsService {

  private readonly roles = Constants.roles;
  private readonly entities = Constants.entities;
  private readonly crtlPathFragment = this.entities.contracts;
  private readonly crtlFullPath = `${environment.apiUrl}/${this.crtlPathFragment}`;
  private readonly loggedUser: any;
  private iAmCustomer? = false;

  constructor(private http: HttpClient, private authService: AuthService, private httpBackend: HttpBackend) {
    this.loggedUser = this.authService.loggedUser();

    this.iAmCustomer = this.loggedUser.role == this.roles.customer;
  }


  public getAllContracts(statuses?: string[]): Observable<Contract[]> {
    const params = UtilsService.buildUrl(statuses);
    return this.http
      .get<ApiResponse>(`${this.crtlFullPath}`, { params })
      .pipe(map(value => value.data as Contract[]));
  }

  public getContract(contractId?: number | string, statuses?: string[]): Observable<Contract> {
    const params = UtilsService.buildUrl(statuses);
    return this.http
      .get<ApiResponse>(`${this.crtlFullPath}/${contractId}`, { params })
      .pipe(map(value => value.data as Contract));
  }

  public getContractByBox(boxId: number) {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.contracts}/${this.entities.box}/${boxId}`)
      .pipe(map(value => value.data as Contract));

  }

  public getAllContractsByCompany(companyId: number, statuses?: string[]): Observable<Contract[]> {
    const params = UtilsService.buildUrl(statuses);
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.companies}/${companyId}/${this.entities.contracts}`, { params })
      .pipe(map(value => value.data as Contract[]));
  }

  public createContract(contract: Contract): Observable<Contract> {

    if (this.iAmCustomer)
      return this.http
        .post<ApiResponse>(`${environment.apiUrl}/${this.entities.companies}/${this.loggedUser.company.id}/${this.entities.contracts}`, contract)
        .pipe(map(value => value.data as Contract));
    else
      return this.http
        .post<ApiResponse>(`${this.crtlFullPath}`, contract)
        .pipe(map(value => value.data as Contract));
  }

  public updateContract(contract: Contract, contractId?: number): Observable<void> {

    return this.http
      .patch<ApiResponse>(`${this.crtlFullPath}/${contractId}`, contract)
      .pipe(map(() => { }));

  }

  public deleteContract(contractId: number): Observable<void> {
    return this.http
      .delete<ApiResponse>(`${this.crtlFullPath}/${contractId}`)
      .pipe(map(() => { }));
  }

  public getAllContractEvents(contractId: number): Observable<ContractEvent[]> {
    return this.http
      .get<ApiResponse>(`${this.crtlFullPath}/${contractId}/${this.entities.contractEvents}`)
      .pipe(map(value => value.data as ContractEvent[]));
  }

  // get output list
  public getContractsOutputList(contractId: number, page_num?: number, page_size?: number): Observable<any> {
    page_num = page_num || 1;
    page_size = page_size || 50;
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/v1/${this.entities.contracts}/${contractId}/${this.entities.executions}?page=${page_num}&page_size=${page_size}`)
  }

  // Retrieve the output of a single execution
  public getContractsOutputTest(contractId: number, eid: string): Observable<any> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/v1/${this.entities.contracts}/${contractId}/${this.entities.executions}/${eid}`)
  }
  // Retrieve report URLs for multiple EIDs
  public getUrlMultipleReports(contractId: number, execIds: string[]): Observable<any> {
    const queryString = execIds.map(id => `exec_ids=${id}`).join('&');
    const url = `${environment.apiUrl}/v1/${this.entities.contracts}/${contractId}/${this.entities.executions}?${queryString}`;
    return this.http.get<ApiResponse>(url);
  }

  // Delete a test in execution
  public deleteTestInExecution(contractId: number, eid: string): Observable<any> {
    return this.http
      .delete<ApiResponse>(`${environment.apiUrl}/v1/${this.entities.contracts}/${contractId}/${this.entities.executions}/${eid}`)
  }

  // Create a workflow execution
  public createExecutionForContract(contractId: number, test: CreateExecutionDTO[]) {
    return this.http
      .post<ApiResponse>(`${environment.apiUrl}/v1/${this.entities.contracts}/${contractId}/${this.entities.executions}`, test)
  }

  // Return the suitable testlist
  public getContractsTestList(contractId: number,  page_num?: number, page_size?: number, search?: string, iface?: string[], cia?: string[], stride?: string[], unece?: string[]) {
    page_num = page_num || 1;
    page_size = page_size || 12;
    search = search || "";
    iface = iface || [];
    cia = cia || [];
    stride = stride || [];
    unece = unece || []

    const ifaceQueryString = iface.map(i => `iface=${encodeURIComponent(i)}`).join('&');
    const ciaQueryString = cia.map(i => `cia=${encodeURIComponent(i)}`).join('&');
    const strideQueryString = stride.map(i => `stride=${encodeURIComponent(i)}`).join('&');
    const uneceQueryString = unece.map(i => `regulations=${encodeURIComponent(i)}`).join('&');

    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/v1/${this.entities.contracts}/${contractId}/${this.entities.testlist}?page_num=${page_num}&page_size=${page_size}&search=${search}${ifaceQueryString ? `&${ifaceQueryString}` : ''}${ciaQueryString ? `&${ciaQueryString}` : ''}${strideQueryString ? `&${strideQueryString}` : ''}${uneceQueryString ? `&${uneceQueryString}` : ''}`);
  }

  //Return available interfaces list for filtering the test list
  public getIntefaceTargetList(contractId: number) {
    return this.http
    .get<ApiResponse>(`${environment.apiUrl}/v1/${this.entities.contracts}/${contractId}/${this.entities.testlist}/${this.entities.targets}`)
  }

  // Return the single test information (in terms of interfaces and input schema)
  public getContractsTest(contractId: number, tid: string) {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/v1/${this.entities.contracts}/${contractId}/${this.entities.testlist}/${tid}`)
  }

  public getGenericOutput(url: string) {
    return this.http
      .get<any>(url)
  }

  public getWesethIfaces(contractId: number, boxId: number): Observable<any> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/v1/${this.entities.contracts}/${contractId}/${this.entities.boxes}/${boxId}`);
  }

  public getTestbedNetworks(contractId: number): Observable<any> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/v1/${this.entities.contracts}/${contractId}/${this.entities.testbed}/${this.entities.networks}`);
  }

  public getContractInputFile(contractId: number): Observable<any> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.contracts}/${contractId}/${this.entities.input_files}`);
  }

  public getUploadSignedUrl(contractId: number, filename: string, size: number): Observable<any> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.contracts}/${contractId}/${this.entities.input_files}/${this.entities.uploadSignedUrl}?filename=${filename}&byteLength=${size}`);
  }

  public uploadFile(arrayBuffer: ArrayBuffer, filename: string, fileType: string, url: string, fields: { [key: string]: string }): Observable<any> {

    const formData = this.buildFormData(url, fields);

    formData.set("file", new Blob([arrayBuffer], { type: fileType }), filename);

    return this.http.post(
      url,
      formData
    );
  }

  buildFormData(url: string, fields: { [p: string]: string }): FormData {
    const formData = new FormData();

      for (const field in fields) {
        formData.set(field, fields[field]);
      }
    
    
    return formData;

  }

}
